import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  signal,
} from '@angular/core';
import {
  Select2ScrollEvent,
  Select2SearchEvent,
  Select2UpdateEvent,
} from 'ng-select2-component';
import { APP } from 'src/app/config/app';
import { UserSortType, UserStatus } from 'src/app/models/enums';
import { UsersService } from 'src/app/services';

const SEARCH_DEBOUNCE_MILLISECONDS = 500;

@Component({
  selector: 'app-driver-select',
  templateUrl: './driver-select.component.html',
  standalone: false,
})
export class DriverSelectComponent implements OnInit, OnChanges, OnDestroy {
  @Input() clientId?: number;
  @Input() clientIds: number[] = [];
  @Input() value?: any;
  // Drivers that must stay selectable regardless of the loaded page: the assigned one, or one just created
  @Input() extraOptions: { value: any; label: string }[] = [];
  @Input() placeholder = '';
  @Input() overlay = false;
  @Input() disabled = false;
  @Output() driverSelected = new EventEmitter<{ value: any; label: string }>();

  readonly drivers = signal<{ value: any; label: string }[]>([]);

  private page = 1;
  private lastPage = false;
  private searchValue?: string;
  private searchTimeout?: ReturnType<typeof setTimeout>;
  private latestRequest = 0;

  constructor(private usersService: UsersService) {}

  ngOnInit() {
    this.drivers.set(this.withExtraOptions([]));
    return this.reload();
  }

  ngOnChanges(changes: SimpleChanges) {
    const clientsChanged = ['clientId', 'clientIds'].some((key) => changes[key] && !changes[key].firstChange);
    if (clientsChanged) this.reload();
    else if (changes['extraOptions'] && !changes['extraOptions'].firstChange) {
      this.drivers.set(this.withExtraOptions(this.drivers()));
    }
  }

  ngOnDestroy() {
    if (this.searchTimeout) clearTimeout(this.searchTimeout);
  }

  onUpdate(event: Select2UpdateEvent<any> | any) {
    const value = event?.value ?? event;
    const label =
      this.drivers().find((driver) => driver.value === value)?.label ?? '';
    this.driverSelected.emit({ value, label });
  }

  async onScroll(event: Select2ScrollEvent) {
    if (event.way !== 'down' || this.lastPage) return;
    this.page += 1;
    await this.load(true);
  }

  onSearch(event: Select2SearchEvent) {
    if (this.searchTimeout) clearTimeout(this.searchTimeout);
    this.searchTimeout = setTimeout(() => {
      this.searchValue = event.search || undefined;
      this.reload();
    }, SEARCH_DEBOUNCE_MILLISECONDS);
  }

  private reload() {
    this.page = 1;
    this.lastPage = false;
    return this.load();
  }

  private async load(append = false) {
    const request = ++this.latestRequest;
    try {
      const driversPage: any = await this.usersService.filterDrivers(
        UserSortType.ALPHABETICALLY,
        this.page,
        APP.DEFAULT_PAGE_SIZE,
        this.clientId ? [this.clientId] : this.clientIds,
        [],
        [UserStatus.ACTIVE],
        this.searchValue,
      );
      // A slower earlier request must not overwrite the list of a newer one
      if (request !== this.latestRequest) return;
      const loadedDrivers = (driversPage?.content ?? []).map((driver: any) => ({
        value: driver.id,
        label: `${driver.firstName ?? ''} ${driver.lastName ?? ''}`.trim(),
      }));
      this.lastPage = !!driversPage?.last;
      this.drivers.set(
        this.withExtraOptions(
          append ? [...this.drivers(), ...loadedDrivers] : loadedDrivers,
        ),
      );
    } catch (error) {
      console.error('Could not load the drivers', error);
      if (request !== this.latestRequest) return;
      if (!append) this.drivers.set([]);
      this.lastPage = true;
    }
  }

  private withExtraOptions(loadedDrivers: { value: any; label: string }[]) {
    const missing = this.extraOptions.filter(
      (option) =>
        option?.value &&
        !loadedDrivers.some((driver) => driver.value === option.value),
    );
    return missing.length > 0 ? [...loadedDrivers, ...missing] : loadedDrivers;
  }
}
